import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getDeviceFingerprint } from "../utils/device.js";

const ADMIN_PATH_PATTERN = /^\/api\/(admin|reports)(\/|$)/;
const CSRF_SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);
const ADMIN_CSRF_COOKIE = "MER-ADMIN-XSRF";
const ADMIN_CSRF_HEADER = "X-Admin-CSRF-Token";
const USER_CSRF_COOKIE = "XSRF-TOKEN";
const USER_CSRF_HEADER = "X-CSRF-Token";

const AuthContext = createContext({
  user: null,
  loading: true,
  adminSessionReady: false,
  login: async () => {},
  completeStepUp: async () => {},
  logout: async () => {},
  signup: async () => {},
  confirmEmail: async () => {},
  authorizedFetch: async (...args) => fetch(...args),
  ensureAdminSession: async () => {},
  lastError: null,
  setLastError: () => {},
  clearError: () => {},
});

async function parseResponse(response) {
  const text = await response.text();
  try {
    return JSON.parse(text || "{}");
  } catch (_err) {
    return { detail: text || response.statusText };
  }
}

function readCookie(name) {
  if (typeof document === "undefined" || !name) {
    return null;
  }
  const entries = document.cookie ? document.cookie.split(";") : [];
  for (const entry of entries) {
    const trimmed = entry.trim();
    if (trimmed.startsWith(`${name}=`)) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }
  return null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastError, setLastError] = useState(null);
  const [adminSessionReady, setAdminSessionReady] = useState(false);
  const adminSessionRequestRef = useRef(null);
  
  // Expose adminSessionReady state
  const adminSessionReadyValue = useMemo(() => adminSessionReady, [adminSessionReady]);

  const clearError = useCallback(() => setLastError(null), []);

  const login = useCallback(async (payload) => {
    clearError();
    const response = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        ...payload,
        device: getDeviceFingerprint(),
      }),
    });
    const data = await parseResponse(response);
    if (!response.ok) {
      // Handle Pydantic validation errors
      let errorMessage = "Unable to sign in";
      if (data.detail) {
        if (Array.isArray(data.detail)) {
          // Pydantic validation errors come as an array
          errorMessage = data.detail.map(err => err.msg || err).join('; ');
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
      }
      setLastError(errorMessage);
      throw new Error(errorMessage);
    }
    
    if (data?.status === "ok") {
      if (data.profile) {
        // Profile included in login response - use it directly
        setUser(data.profile);
      } else if (data.step_up_token) {
        // Step-up challenge required - don't set user yet
        // The step-up flow will handle setting the user
      } else {
        // Profile not in response - this shouldn't happen for successful login
        // but try to fetch it as fallback
        try {
          const meResponse = await fetch("/api/auth/me", {
            method: "GET",
            credentials: "include",
          });
          if (meResponse.ok) {
            const meData = await parseResponse(meResponse);
            if (meData?.user) {
              setUser(meData.user);
            }
          }
        } catch (error) {
          // Silently fail - user might need to refresh
        }
      }
    } else if (data?.status === "step_up_required") {
      // Step-up required - don't set user yet
      // Code will be sent via email, user enters it in the form
    }
    return data;
  }, [clearError]);

  const logout = useCallback(async () => {
    clearError();
    const csrfToken = readCookie(USER_CSRF_COOKIE);
    const response = await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "include",
      headers: csrfToken ? { [USER_CSRF_HEADER]: csrfToken } : undefined,
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        setUser(null);
        return;
      }
      const data = await parseResponse(response);
      // Handle Pydantic validation errors
      let errorMessage = "Unable to sign out";
      if (data.detail) {
        if (Array.isArray(data.detail)) {
          // Pydantic validation errors come as an array
          errorMessage = data.detail.map(err => err.msg || err).join('; ');
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
      }
      setLastError(errorMessage);
      throw new Error(errorMessage);
    }
    // Force complete page reload to clear all state, cookies, and caches
    if (typeof window !== "undefined") {
      window.location.href = "/";
      return;
    }
    setUser(null);
  }, [clearError]);

  const signup = useCallback(async (payload) => {
    clearError();
    const response = await fetch("/api/auth/signup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      credentials: "include",
    });
    const data = await parseResponse(response);
    if (!response.ok) {
      // Handle Pydantic validation errors
      let errorMessage = "Unable to sign up";
      if (data.detail) {
        if (Array.isArray(data.detail)) {
          // Pydantic validation errors come as an array
          // Extract field-specific errors and format them nicely
          const fieldErrors = {};
          let generalError = null;
          
          data.detail.forEach(err => {
            const field = err.loc[err.loc.length - 1]; // Get the last element (field name)
            if (field === 'nickname') {
              fieldErrors.nickname = "Nickname must be: letters a-z, numbers 0-9, and dash/underscore only";
            } else if (field === 'email') {
              fieldErrors.email = err.msg;
            } else if (field === 'password') {
              fieldErrors.password = err.msg;
            } else {
              generalError = err.msg;
            }
          });
          
          // If we have specific field errors, throw them in a structured way
          if (Object.keys(fieldErrors).length > 0) {
            const error = new Error('validation_error');
            error.fieldErrors = fieldErrors;
            error.generalError = generalError;
            throw error;
          }
          
          errorMessage = generalError || data.detail.map(err => err.msg).join('; ');
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
      }
      setLastError(errorMessage);
      throw new Error(errorMessage);
    }
    return data;
  }, [clearError]);

  const confirmEmail = useCallback(async ({ email, code }) => {
    clearError();
    const response = await fetch("/api/auth/verify-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, code }),
      credentials: "include",
    });
    const data = await parseResponse(response);
    if (!response.ok) {
      // Handle Pydantic validation errors
      let errorMessage = "Unable to verify email";
      if (data.detail) {
        if (Array.isArray(data.detail)) {
          // Pydantic validation errors come as an array
          errorMessage = data.detail.map(err => err.msg || err).join('; ');
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
      }
      setLastError(errorMessage);
      throw new Error(errorMessage);
    }
    return data;
  }, [clearError]);

  const completeStepUp = useCallback(async (payload) => {
    clearError();
    
    // Validate and sanitize payload
    const sanitizedPayload = {
      email: payload.email?.trim(),
      step_up_token: payload.step_up_token?.trim(),
      code: payload.code?.trim().replace(/\D/g, '').slice(0, 6), // Only digits, exactly 6 chars
      device: getDeviceFingerprint(),
    };
    
    // Validate required fields
    if (!sanitizedPayload.email || !sanitizedPayload.step_up_token || !sanitizedPayload.code) {
      throw new Error("Missing required fields for step-up verification");
    }
    
    if (sanitizedPayload.code.length !== 6) {
      throw new Error(`Verification code must be exactly 6 digits (got ${sanitizedPayload.code.length})`);
    }
    
    const response = await fetch("/api/auth/step-up", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(sanitizedPayload),
      credentials: "include",
    });
    const data = await parseResponse(response);
    if (!response.ok) {
      // Handle Pydantic validation errors
      let errorMessage = "Unable to confirm device";
      if (data.detail) {
        if (Array.isArray(data.detail)) {
          // Pydantic validation errors come as an array
          errorMessage = data.detail.map(err => err.msg || err).join('; ');
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
      }
      setLastError(errorMessage);
      throw new Error(errorMessage);
    }
    if (data?.status === "ok" && data.profile) {
      setUser(data.profile);
    }
    return data;
  }, [clearError]);

  const refreshSession = useCallback(async () => {
    try {
      const response = await fetch("/api/auth/me", {
        method: "GET",
        credentials: "include",
      });
      if (!response.ok) {
        setUser(null);
        return null;
      }
      const data = await parseResponse(response);
      if (data?.user) {
        setUser(data.user);
        return data.user;
      }
      setUser(null);
      return null;
    } catch (error) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "owner") {
      setAdminSessionReady(Boolean(readCookie(ADMIN_CSRF_COOKIE)));
    } else {
      setAdminSessionReady(false);
      adminSessionRequestRef.current = null;
    }
  }, [user?.role]);

  const ensureAdminSession = useCallback(async () => {
    if (user?.role !== "admin" && user?.role !== "owner") {
      return false;
    }
    if (adminSessionReady && readCookie(ADMIN_CSRF_COOKIE)) {
      return true;
    }
    if (adminSessionRequestRef.current) {
      return adminSessionRequestRef.current;
    }
    
    // Create a timeout promise
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error("Admin session upgrade timed out")), 10000);
    });
    
    const userCsrf = readCookie(USER_CSRF_COOKIE);
    const fetchPromise = fetch("/api/admin/session/upgrade", {
      method: "POST",
      credentials: "include",
      headers: userCsrf ? { [USER_CSRF_HEADER]: userCsrf } : undefined,
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage = "Unable to initialize admin session";
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.detail || errorData.message || errorMessage;
          } catch {
            if (errorText) {
              errorMessage = errorText;
            }
          }
          // Provide more specific error messages
          if (response.status === 401) {
            errorMessage = "Authentication required. Please log in again.";
          } else if (response.status === 403) {
            errorMessage = "Admin access denied. Your account may not have admin privileges.";
          }
          throw new Error(errorMessage);
        }
        // Verify CSRF cookie was set
        const csrfCookie = readCookie(ADMIN_CSRF_COOKIE);
        if (!csrfCookie) {
          throw new Error("Admin session upgrade succeeded but CSRF token not set");
        }
        setAdminSessionReady(true);
        return true;
      })
      .catch((error) => {
        setAdminSessionReady(false);
        console.error("Admin session upgrade failed:", error);
        throw error;
      })
      .finally(() => {
        adminSessionRequestRef.current = null;
      });
    
    const request = Promise.race([fetchPromise, timeoutPromise]);
    adminSessionRequestRef.current = request;
    return request;
  }, [adminSessionReady, user?.role]);

  const authorizedFetch = useCallback(
    async (input, init = {}) => {
      const requestInit = {
        ...init,
      };
      const originalUrl =
        typeof input === "string"
          ? input
          : input?.url ?? (typeof Request !== "undefined" && input instanceof Request ? input.url : "");
      const base =
        typeof window !== "undefined" && window.location ? window.location.origin : "http://localhost";
      let pathname = "";
      try {
        pathname = new URL(originalUrl || "", base).pathname;
      } catch (_err) {
        pathname = typeof originalUrl === "string" ? originalUrl : "";
      }
      const isAdminRequest = ADMIN_PATH_PATTERN.test(pathname);
      if (isAdminRequest) {
        await ensureAdminSession();
      }

      const isRequestObject = typeof Request !== "undefined" && input instanceof Request;
      const headers = new Headers(init.headers || (isRequestObject ? input.headers : undefined));
      const method = (requestInit.method || (isRequestObject ? input.method : "GET") || "GET")
        .toString()
        .toUpperCase();
      
      // Add CSRF token for non-safe methods
      if (!CSRF_SAFE_METHODS.has(method)) {
        if (isAdminRequest) {
          const csrfToken = readCookie(ADMIN_CSRF_COOKIE);
          if (csrfToken) {
            headers.set(ADMIN_CSRF_HEADER, csrfToken);
          }
        } else {
          const csrfToken = readCookie(USER_CSRF_COOKIE);
          if (csrfToken) {
            headers.set(USER_CSRF_HEADER, csrfToken);
          }
        }
      }
      
      requestInit.headers = headers;
      requestInit.credentials = init.credentials ?? "include";

      return fetch(input, requestInit);
    },
    [ensureAdminSession],
  );

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const value = useMemo(() => ({
    user,
    loading,
    adminSessionReady: adminSessionReadyValue,
    login,
    completeStepUp,
    logout,
    signup,
    confirmEmail,
    refreshSession,
    authorizedFetch,
    ensureAdminSession,
    lastError,
    setLastError,
    clearError,
  }), [
    user,
    loading,
    adminSessionReadyValue,
    login,
    completeStepUp,
    logout,
    signup,
    confirmEmail,
    refreshSession,
    authorizedFetch,
    ensureAdminSession,
    lastError,
    clearError,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
